import { useCallback, useRef } from 'react';
import { log } from 'log';
import { useGetErrorMessage } from 'js/hooks/server';
import { useCheckUsernameAvailability } from 'server';
import { TValidationMethod } from 'js/utils/Validation/types';
import { ICheckUsernameAvailabilityResponseData } from 'js/hooks/socket/hooks/useCheckUsernameAvailability/types';

import * as hooks from './hooks';
import * as Types from './types';
import { useWithRecordLevelValidation } from '../useWithRecordLevelValidation';

export const useValidateUserName = ({
    fieldName,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest,
    updateFieldValue
}: Types.IUseValidateUserNameOptions) => {
    const abortController = useRef<AbortController | null>(null);
    const cachedUserName = useRef<string | null>(null);
    const cachedRecaptchaValidationRequest = useRef<Promise<{ token: string }> | null>(null);
    const cachedServerResponse = useRef<ICheckUsernameAvailabilityResponseData | null>(null);
    const getErrorMessage = useGetErrorMessage();
    const checkIfUsernameIsAvailable = useCheckUsernameAvailability();
    const renderErrorMessage = hooks.useRenderErrorMessage({ updateFieldValue });
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName });

    // this has to be async function so that React Final Form will wait for resolution
    const asyncServerRequest = useCallback(async function process(userName: string, validationToken?: string):
        Promise<Types.IUseValidateUserNameResponse> {
        try {
            const response = await checkIfUsernameIsAvailable({
                userName,
                ...validationToken && { token: validationToken }
            });

            const { usernameAvailable, suggestions = [] } = response;

            cachedUserName.current = userName;
            cachedServerResponse.current = response;
            cachedRecaptchaValidationRequest.current = null;
            abortController.current?.abort();

            if (!usernameAvailable) {
                return renderErrorMessage(suggestions);
            }
        } catch (error: Types.ICheckIfUsernameIsAvailableErrorResponse | any) {
            const { data: { errorCode } } = error.response;

            if (errorCode === 301) {
                try {
                    abortController.current = new AbortController();
                    abortController.current?.signal.addEventListener('abort', abortReCaptchaTokenRequest);

                    const tokenResponse = requestReCaptchaToken();

                    cachedRecaptchaValidationRequest.current = tokenResponse;

                    const { token } = await tokenResponse;

                    return await process(userName, token);
                } catch (validationError) {
                    log('error', validationError);

                    return getErrorMessage(validationError);
                }
            }

            return getErrorMessage(error);
        }
    }, [
        getErrorMessage,
        renderErrorMessage,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest,
        checkIfUsernameIsAvailable
    ]);

    // this has to be async function so that React Final Form will wait for resolution
    const asyncCachedRequest = useCallback(
        async (userName: string, recaptchaValidationPromise: Promise<{ token: string }>) => {
            let recaptchaToken;

            try {
                const data = await recaptchaValidationPromise;

                recaptchaToken = data.token;
            } catch {
                return await asyncServerRequest(userName);
            }

            return await asyncServerRequest(userName, recaptchaToken);
        }, [asyncServerRequest]);

    const userNameValidate = useCallback<TValidationMethod<string>>(async (userName) => {
        if (!userName) {
            return;
        }

        if (cachedUserName.current === userName) {
            const { usernameAvailable = true, suggestions = [] } = cachedServerResponse.current ?? {};

            if (usernameAvailable) {
                return;
            }

            return renderErrorMessage(suggestions);
        }

        if (cachedRecaptchaValidationRequest.current) {
            return asyncCachedRequest(userName, cachedRecaptchaValidationRequest.current);
        }

        return asyncServerRequest(userName);
    }, [
        asyncServerRequest,
        renderErrorMessage,
        asyncCachedRequest
    ]);

    return withRecordLevelValidation(userNameValidate);
};
